import { Divider, Stack } from '@mui/material';
import { memo, useCallback, useMemo, useRef } from 'react';
import { t } from 'i18next';
import { useShallow } from 'zustand/react/shallow';
import { LogicalSpace, Obstacle, Point, Polygon } from '@thive/fddm-types';

import { Dimensions } from '@/modules/common/components/Dimensions';
import { Accordion } from '@/modules/common/components/Accordion';
import { NumUtils } from '@/modules/common/utils';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { areasToFlatCollection, isHighwayArea } from '@/modules/floorplanDesign/helpers';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useReadSelectedGroups } from '@/modules/floorplanDesign/hooks/useReadSelectedGroups';
import { Area } from '@/modules/floorplanDesign/types';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { computeRectanglesCenterBoundingBox } from '@/modules/common/helpers/boundingBox';

type GroupDimensionsValue = {
  x: number;
  y: number;
  length: number;
  width: number;
};

type GroupDimensionsProps = GroupDimensionsValue & {
  disabled: boolean;
  onXChange: (value: number) => void;
  onYChange: (value: number) => void;
};

const pointsToPolygon = (points: Point[]): Polygon | null => {
  const [first, second, third, ...rest] = points;

  if (!first || !second || !third) {
    return null;
  }

  return {
    points: [first, second, third, ...rest],
  };
};

const moveArea = (area: Area, deltaX: number, deltaY: number): Area => {
  if (isHighwayArea(area)) {
    return {
      ...area,
      segments: area.segments.map((segment) => ({
        ...segment,
        start: {
          x: segment.start.x + deltaX,
          y: segment.start.y + deltaY,
        },
        end: {
          x: segment.end.x + deltaX,
          y: segment.end.y + deltaY,
        },
      })) as typeof area.segments,
    };
  }

  return {
    ...area,
    rectangle: {
      ...area.rectangle,
      centerX: area.rectangle.centerX + deltaX,
      centerY: area.rectangle.centerY + deltaY,
    },
  };
};

const moveObstacle = (obstacle: Obstacle, deltaX: number, deltaY: number): Obstacle => ({
  ...obstacle,
  segments: obstacle.segments.map((segment) => ({
    ...segment,
    start: {
      x: segment.start.x + deltaX,
      y: segment.start.y + deltaY,
    },
    end: {
      x: segment.end.x + deltaX,
      y: segment.end.y + deltaY,
    },
  })) as typeof obstacle.segments,
});

const moveLogicalSpace = (
  logicalSpace: LogicalSpace,
  deltaX: number,
  deltaY: number,
): LogicalSpace | null => {
  const rectangle = RectangleUtils.polygonToRectangle(logicalSpace.polygon);

  if (rectangle) {
    const movedRectangle = {
      ...rectangle,
      centerX: rectangle.centerX + deltaX,
      centerY: rectangle.centerY + deltaY,
    };

    const points = RectangleUtils.rectangleToPolygon(movedRectangle).map((point) => ({
      x: point.x,
      y: point.y,
    }));

    const polygon = pointsToPolygon(points);
    if (!polygon) return null;

    return {
      ...logicalSpace,
      polygon,
    };
  }

  const movedPoints = logicalSpace.polygon.points.map((point) => ({
    x: point.x + deltaX,
    y: point.y + deltaY,
  }));

  const polygon = pointsToPolygon(movedPoints);
  if (!polygon) return null;

  return {
    ...logicalSpace,
    polygon,
  };
};

function GroupDimensionsComponent({
  x,
  y,
  length,
  width,
  disabled,
  onXChange,
  onYChange,
}: GroupDimensionsProps) {
  return (
    <Accordion
      title={t<string>('interface:properties.dimensions.label', 'Dimensions')}
      unmountOnExit
      defaultExpanded
      sx={{ px: 2 }}
    >
      <Dimensions
        disabled={disabled}
        disableLength
        disableWidth
        disableAngle
        x={NumUtils.metersToMmRounded(x)}
        y={NumUtils.metersToMmRounded(y)}
        length={NumUtils.metersToMmRounded(length)}
        width={NumUtils.metersToMmRounded(width)}
        onXChange={onXChange}
        onYChange={onYChange}
      />
    </Accordion>
  );
}

const GroupDimensions = memo(GroupDimensionsComponent);

function GroupParametersComponent() {
  const selectedGroups = useReadSelectedGroups();
  const mode = useAppStore((state) => state.mode);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const group = useMemo(() => {
    if (selectedGroups.length !== 1) return undefined;

    return selectedGroups[0];
  }, [selectedGroups]);

  const dimensions = useFloorplanDesignStore(
    useShallow((state) => {
      if (!group) return null;

      const groupItemIds = new Set(group.areaIds);
      const areasInGroup = areasToFlatCollection(state.getAreasByIds(group.areaIds));
      const logicalSpacesInGroup = state.design.logicalSpaces.filter((logicalSpace) =>
        groupItemIds.has(logicalSpace.id),
      );
      const obstaclesInGroup = state.design.obstacles.filter((obstacle) =>
        groupItemIds.has(obstacle.id),
      );

      const areaRectangles = areasInGroup.flatMap((item) =>
        isHighwayArea(item) ? item.segments.flatMap(RectangleUtils.fromSegment) : [item.rectangle],
      );

      const obstacleRectangles = obstaclesInGroup.flatMap((obstacle) =>
        obstacle.segments.flatMap(RectangleUtils.fromSegment),
      );

      const logicalSpaceRectangles = logicalSpacesInGroup.flatMap((logicalSpace) => {
        const rectangle = RectangleUtils.polygonToRectangle(logicalSpace.polygon);
        return rectangle ? [rectangle] : [];
      });

      const rectangles = [...areaRectangles, ...logicalSpaceRectangles, ...obstacleRectangles];

      if (rectangles.length === 0) return null;

      const box = computeRectanglesCenterBoundingBox(rectangles);
      return { x: box.centerX, y: box.centerY, length: box.width, width: box.height };
    }),
  );

  const currentDimensionsRef = useRef<GroupDimensionsValue | null>(null);
  // plz fix me
  // eslint-disable-next-line react-hooks/set-state-in-effect, react-hooks/refs
  currentDimensionsRef.current = dimensions;

  const moveGroup = useCallback(
    (deltaX: number, deltaY: number) => {
      if (!group) return;
      if (mode !== 'EDITABLE') return;

      const state = useFloorplanDesignStore.getState();
      const groupItemIds = new Set(group.areaIds);

      const areasInGroup = areasToFlatCollection(state.getAreasByIds(group.areaIds));
      const logicalSpacesInGroup = state.design.logicalSpaces.filter((logicalSpace) =>
        groupItemIds.has(logicalSpace.id),
      );
      const obstaclesInGroup = state.design.obstacles.filter((obstacle) =>
        groupItemIds.has(obstacle.id),
      );

      const areaUpdates = createEmptyAreas();

      areasInGroup.forEach((area) => {
        pushAreaToItsCollection(areaUpdates, moveArea(area, deltaX, deltaY));
      });

      const logicalSpaceUpdates = logicalSpacesInGroup
        .map((logicalSpace) => moveLogicalSpace(logicalSpace, deltaX, deltaY))
        .filter((logicalSpace): logicalSpace is LogicalSpace => Boolean(logicalSpace));

      const obstacleUpdates = obstaclesInGroup.map((obstacle) =>
        moveObstacle(obstacle, deltaX, deltaY),
      );

      if (areasToFlatCollection(areaUpdates).length > 0) {
        fddmSvc.updateAreas(areaUpdates);
      }

      logicalSpaceUpdates.forEach((logicalSpace) => {
        fddmSvc.updateLogicalSpace(logicalSpace);
      });

      if (obstacleUpdates.length > 0) {
        fddmSvc.updateObstacles(obstacleUpdates);
      }
    },
    [fddmSvc, group, mode],
  );

  const onXChange = useCallback(
    (value: number) => {
      if (!currentDimensionsRef.current) return;

      const nextX = NumUtils.mmToMetersRoundedToMm(value);
      const deltaX = nextX - currentDimensionsRef.current.x;

      if (deltaX === 0) return;

      moveGroup(deltaX, 0);
    },
    [moveGroup],
  );

  const onYChange = useCallback(
    (value: number) => {
      if (!currentDimensionsRef.current) return;

      const nextY = NumUtils.mmToMetersRoundedToMm(value);
      const deltaY = nextY - currentDimensionsRef.current.y;

      if (deltaY === 0) return;

      moveGroup(0, deltaY);
    },
    [moveGroup],
  );

  if (!dimensions) return null;

  return (
    <Stack spacing={1} divider={<Divider />}>
      <GroupDimensions
        disabled={mode !== 'EDITABLE'}
        x={dimensions.x}
        y={dimensions.y}
        length={dimensions.length}
        width={dimensions.width}
        onXChange={onXChange}
        onYChange={onYChange}
      />
    </Stack>
  );
}

export const GroupParameters = memo(GroupParametersComponent);
